import React, { useEffect, useState, useCallback } from "react";
import { auditApi } from "./api";
import { Breadcrumbs } from "./AdminLayout";
import { Download, X, Filter } from "lucide-react";

const EMPTY = { action: "", entity_type: "", correlation_id: "", from_ts: "", to_ts: "", q: "", limit: 200 };

export default function AdminAudit() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(EMPTY);
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== "" && v !== null));
    auditApi.list(params).then(setRows).catch(()=>{});
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const clear = () => setFilters(EMPTY);
  const csvHref = auditApi.csvUrl(Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== "" && v !== null)));

  return (
    <div data-testid="admin-audit">
      <Breadcrumbs items={["Admin", "Audit Log"]}/>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-navy tracking-tight">Audit Log</h1>
          <p className="text-stone2 text-sm mt-1">Every admin action, correlation-tracked. Includes scheduled operations by the <span className="mono">System</span> actor.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="toggle-filters" onClick={()=>setShowFilters(v=>!v)} className="border border-line text-navy text-sm px-4 py-2 inline-flex items-center gap-2">
            <Filter size={14} strokeWidth={1.5}/> {showFilters ? "Hide filters" : "Filters"}
          </button>
          <a data-testid="audit-csv-download" href={csvHref} className="border border-navy text-navy text-sm px-4 py-2 inline-flex items-center gap-2 hover:bg-navy hover:text-white transition-colors">
            <Download size={14} strokeWidth={1.5}/> Export CSV
          </a>
        </div>
      </div>

      {showFilters && (
        <div data-testid="audit-filters" className="bg-white border border-line p-5 mb-6 grid md:grid-cols-4 gap-4">
          <Field label="Action contains"><input data-testid="f-action" value={filters.action} onChange={e=>set("action", e.target.value)} className={inputCls}/></Field>
          <Field label="Entity type"><input data-testid="f-entity" value={filters.entity_type} onChange={e=>set("entity_type", e.target.value)} className={inputCls}/></Field>
          <Field label="Correlation ID"><input data-testid="f-corr" value={filters.correlation_id} onChange={e=>set("correlation_id", e.target.value)} className={inputCls}/></Field>
          <Field label="Free text (q)"><input data-testid="f-q" value={filters.q} onChange={e=>set("q", e.target.value)} className={inputCls}/></Field>
          <Field label="From (ISO)"><input data-testid="f-from" placeholder="2026-01-01" value={filters.from_ts} onChange={e=>set("from_ts", e.target.value)} className={inputCls}/></Field>
          <Field label="To (ISO)"><input data-testid="f-to" placeholder="2026-12-31" value={filters.to_ts} onChange={e=>set("to_ts", e.target.value)} className={inputCls}/></Field>
          <div className="col-span-2 flex items-end gap-2">
            <button data-testid="clear-filters" onClick={clear} className="mono text-[10px] uppercase tracking-widest text-stone2 hover:text-[#9F1239] inline-flex items-center gap-1"><X size={12}/> Clear all</button>
            <span className="mono text-[10px] uppercase tracking-widest text-stone2 ml-auto">{rows.length} rows</span>
          </div>
        </div>
      )}

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-clinical">
            <tr className="text-left mono text-[10px] uppercase tracking-widest text-stone2">
              <th className="px-4 py-3 border-b border-line">Timestamp</th>
              <th className="px-4 py-3 border-b border-line">Actor</th>
              <th className="px-4 py-3 border-b border-line">Action</th>
              <th className="px-4 py-3 border-b border-line">Entity</th>
              <th className="px-4 py-3 border-b border-line">IP</th>
              <th className="px-4 py-3 border-b border-line">Correlation</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`audit-${r.id}`} className="border-b border-line last:border-0 hover:bg-clinical/40">
                <td className="px-4 py-2.5 mono text-xs text-stone2 whitespace-nowrap">{r.timestamp?.slice(0,19).replace('T',' ')}</td>
                <td className="px-4 py-2.5">
                  <div className="text-navy font-medium">{r.actor_name}</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-stone2">{r.actor_role || r.actor_type}</div>
                </td>
                <td className="px-4 py-2.5 mono text-xs text-navy">{r.action}</td>
                <td className="px-4 py-2.5 text-xs text-stone2">{r.entity_type || "—"}{r.entity_id ? ` · ${r.entity_id.slice(0,8)}` : ""}</td>
                <td className="px-4 py-2.5 mono text-xs text-stone2">{r.ip || "—"}</td>
                <td className="px-4 py-2.5 mono text-[10px] text-stone2" title={r.correlation_id}>{r.correlation_id?.slice(0,8) || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan="6" className="px-4 py-6 text-center text-stone2">No entries match your filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return (<label className="block"><div className="mono text-[10px] uppercase tracking-widest text-stone2 mb-1.5">{label}</div>{children}</label>);
}
const inputCls = "w-full bg-white border border-line px-3 py-2 text-sm focus:outline-none focus:border-navy";
